import React, { useState, useContext, useCallback, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import queryString from "query-string";
import { useParams } from "react-router-dom";
import { SelectPagination } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { Context } from "../../context/GroupsManagerContext";
import { Types } from "../../context/filters.reducer";
import { groupsSearch } from "../../graphql/groups.query";
import { groupAdapter } from "../../adapters/group";
import { AdaptedGroup, SelectableOption } from "../../models";
import { useTranslation } from "react-i18next";

const REQUEST_SIZE = 20;

const CrnSelector: React.FC = () => {
  const { state, dispatch } = useContext(Context);
  const { t } = useTranslation();
  const client = useApolloClient();
  const [selectedCrn, setSelectedCrn] = useState<SelectableOption<AdaptedGroup>>(null);
  const [page, setPage] = useState(0);
  const [, setSearchTerm] = useState("");
  const [prevSearchTerm, setPrevSearchTerm] = useState("");
  const { origin, scenario }: IParams = useParams();
  const params: queryString.ParsedQuery<string> = queryString.parse(location.search);

  useEffect(() => {
    dispatch({ type: Types.SelectCRN, payload: selectedCrn?.self });
  }, [selectedCrn]);

  /**
   * If the user clicks the `Borrar selección` button, reset the selected CRN.
   */
  useEffect(() => {
    if (!state?.filters?.selectedCrn && selectedCrn?.self?.code) {
      setSelectedCrn(null);
    }
  }, [state?.filters?.selectedCrn]);

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      try {
        const variables = {
          query: groupsSearch,
          variables: {
            scenarioId: scenario,
            originId: origin,
            filterId: params?.advance,
            filter: {
              pagination: { page, size: REQUEST_SIZE },
              fields: searchTerm
                ? {
                    clientCode: { contains: searchTerm },
                  }
                : undefined,
            },
          },
        };

        const { data } = await client.query(variables);
        const cube = data?.cube;
        const pageInfo = cube?.groupsManagerSearch?.pageInfo;
        const adaptedGroups = (cube?.groupsManagerSearch?.items ?? [])?.map(groupAdapter);

        const options = (adaptedGroups ?? [])?.map(adaptedGroup => ({
          id: adaptedGroup?.id,
          value: adaptedGroup?.id,
          label: adaptedGroup?.clientCode || adaptedGroup?.code || "",
          self: adaptedGroup,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = prevSearchTerm === newSearchTerm ? page + 1 : 1;

    setPrevSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(newSearchTerm, newSearchPage);

    return {
      options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  return (
    <SelectPagination
      label={t("groups-manager.general-filters.search-by.crn")}
      placeholder={t("groups-manager.general-filters.search-by.crn-placeholder")}
      isDisabled={false}
      value={selectedCrn}
      onInputChange={setSearchTerm}
      onChange={setSelectedCrn}
      loadOptions={loadOptions}
      debounceTimeout={500}
      noOptionsMessage={() => t("groups-manager.general-filters.search-by.no-options")}
    />
  );
};

export default CrnSelector;
